import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { I18nField, ImagePicker } from './AdminHero';
import { Plus, Save, Trash2, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const AdminProducts = () => {
  const { refresh } = useSiteConfig();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await api.get('/admin/products');
    setItems(r.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const update = (idx, patch) => setItems((a) => a.map((x, i) => i === idx ? { ...x, ...patch } : x));

  const addNew = async () => {
    const r = await api.post('/admin/products', {
      name: { de: 'Neues Produkt', en: 'New Product', ar: 'منتج جديد' },
      description: { de: '', en: '', ar: '' },
      image: null, price: '', order: items.length, visible: true,
    });
    setItems((a) => [...a, r.data]);
    refresh();
  };

  const save = async (p) => {
    try { await api.put(`/admin/products/${p.id}`, p); toast.success('Saved'); refresh(); }
    catch { toast.error('Save failed'); }
  };

  const remove = async (p) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/admin/products/${p.id}`);
    setItems((a) => a.filter((x) => x.id !== p.id));
    refresh();
  };

  const move = async (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= items.length) return;
    const a = { ...items[idx], order: ni };
    const b = { ...items[ni], order: idx };
    setItems((arr) => arr.map((x, i) => i === idx ? a : i === ni ? b : x).sort((x, y) => x.order - y.order));
    await Promise.all([api.put(`/admin/products/${a.id}`, a), api.put(`/admin/products/${b.id}`, b)]);
    refresh();
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-[#5a1d3b]" style={{ fontWeight: 500 }}>Products</h1>
          <p className="text-gray-500 text-sm">Manage products shown on /produkte page.</p>
        </div>
        <Button onClick={addNew} className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full h-11 px-5 gap-2">
          <Plus className="w-4 h-4" /> Add Product
        </Button>
      </div>

      <div className="space-y-4">
        {items.map((p, idx) => (
          <details key={p.id} className="bg-white border border-gray-200 rounded-xl" open={idx === 0}>
            <summary className="px-5 py-4 cursor-pointer flex items-center gap-3 list-none">
              {p.image && <img src={p.image} alt="" className="w-10 h-10 object-cover rounded" />}
              <span className="flex-1 font-medium text-[#5a1d3b]">{p.name?.de || p.name?.en || 'Untitled'}</span>
              {p.price && <span className="text-sm text-[#c4477a] font-medium">{p.price}</span>}
              {!p.visible && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500">Hidden</span>}
              <button onClick={(e) => { e.preventDefault(); move(idx, -1); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-500" disabled={idx === 0}>
                <ArrowUp className="w-4 h-4" />
              </button>
              <button onClick={(e) => { e.preventDefault(); move(idx, 1); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-500" disabled={idx === items.length - 1}>
                <ArrowDown className="w-4 h-4" />
              </button>
            </summary>
            <div className="px-5 pb-5 pt-2 border-t border-gray-100 space-y-5">
              <I18nField label="Name" value={p.name} onChange={(v) => update(idx, { name: v })} />
              <I18nField label="Description" value={p.description} onChange={(v) => update(idx, { description: v })} multiline />
              <div>
                <Label className="mb-2 block text-sm">Price</Label>
                <Input value={p.price || ''} onChange={(e) => update(idx, { price: e.target.value })} placeholder="e.g. 29.90 €" className="h-10" />
              </div>
              <ImagePicker label="Product Image" value={p.image} onChange={(v) => update(idx, { image: v })} mode="cover" big />
              <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                <button onClick={() => update(idx, { visible: !p.visible })} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#c4477a]">
                  {p.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {p.visible ? 'Visible' : 'Hidden'}
                </button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => remove(p)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                  <Button onClick={() => save(p)} className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full px-5">
                    <Save className="w-4 h-4 mr-1.5" /> Save
                  </Button>
                </div>
              </div>
            </div>
          </details>
        ))}
        {items.length === 0 && <p className="text-center text-gray-500 py-12">No products yet. Click &quot;Add Product&quot; above.</p>}
      </div>
    </div>
  );
};

export default AdminProducts;
